import dedent from 'ts-dedent'
import {
  ColumnDataType,
  GetOutputSchemaParams,
  Provider,
  TableSchema,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType
} from '../types'
import { addColumn } from '../utils/schemas'
import { SelectWorkflowComponent } from './common/selectcomponent'

class Difference extends SelectWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.difference',
        group: 'Joins',
        title: 'ST Difference',
        description: `
      This component calculates the difference between the geographies
      of the input table and the combined boundary of the geographies in the second table.
      `,
        starred: true
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        },
        {
          name: 'polygons',
          type: WorkflowComponentParamType.InputTable,
          title: 'Polygons table'
        },
        {
          name: 'geosource',
          type: WorkflowComponentParamType.Column,
          title: 'Geo column in source table',
          parent: 'source',
          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
        },
        {
          name: 'geopolygons',
          type: WorkflowComponentParamType.Column,
          title: 'Geo column in polygons table',
          parent: 'polygons',
          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
        }
      ]
    )
  }

  getInnerSql({ params, connection }: WorkflowComponentCodeParams) {
    switch (connection.type) {
      case Provider.BigQuery:
        return dedent`SELECT
                  ST_DIFFERENCE(s.${params.geosource}, polyg.geo)
                    AS ${params.geosource}_diff, s.*
                  FROM ${connection.quoteName(params.source as string)} s,
                    (
                      SELECT ST_UNION_AGG(${params.geopolygons}) geo
                      FROM \`${params.polygons}\`
                    ) polyg
                  WHERE ST_INTERSECTS(polyg.geo, s.${params.geosource})`
      case Provider.Snowflake:
        return dedent`SELECT
                  ST_DIFFERENCE(s.${params.geosource}, polyg.geo)
                    AS ${params.geosource}_diff, s.*
                  FROM ${connection.quoteName(params.source as string)} s,
                    (
                      SELECT ST_COLLECT(${params.geopolygons}) geo
                      FROM ${params.polygons}
                    ) polyg
                  WHERE ST_INTERSECTS(polyg.geo, s.${params.geosource})`
      case Provider.Postgres:
        return dedent`SELECT
                  ST_DIFFERENCE(s.${params.geosource}, polyg.geo)
                    AS ${params.geosource}_diff, s.*
                  FROM ${connection.quoteName(params.source as string)} s,
                    (
                      SELECT ST_COLLECT(${params.geopolygons}) geo
                      FROM ${params.polygons}
                    ) polyg
                  WHERE ST_INTERSECTS(polyg.geo, s.${params.geosource})`
      case Provider.Oracle:
        return dedent`SELECT
                  SDO_GEOM.SDO_DIFFERENCE(s.${params.geosource}, polyg.geo, 0.005)
                    AS ${params.geosource}_diff, s.*
                  FROM ${connection.quoteName(params.source as string)} s,
                    (
                      SELECT SDO_AGGR_UNION(SDOAGGRTYPE(${params.geopolygons}, 0.005)) geo
                      FROM ${connection.quoteName(params.polygons as string)}
                    ) polyg
                  WHERE SDO_RELATE(s.${params.geosource}, polyg.geo, 'mask=ANYINTERACT') = 'TRUE'`
      default:
        return null
    }
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema({ columns, params, connection }: GetOutputSchemaParams): TableSchema {
    return addColumn(columns.source, {
      name: `${params.geosource}_diff`,
      type: connection.getGeoDataType()
    })
  }
  supportedProviders(): Provider[] {
    return [Provider.BigQuery, Provider.Postgres, Provider.Snowflake, Provider.Oracle]
  }
}

export { Difference }
